import { TenantInterface, UserInterface, UserVarKey, UserVarsMap } from "@swivl-mono/types";
import { AnimatePresence } from "motion/react";
import { useEffect, useMemo, useState } from "react";

import { DetailsPanel } from "../../../../../Components/Conversations/DetailsPanel/DetailsPanel";
import { DataCardLocation } from "../../../../../Components/DataCards/DataCard.Location";
import { DataCardTenant } from "../../../../../Components/DataCards/DataCard.Tenant";
import Pill, { PillSize, PillStyle, PillType } from "../../../../../Components/Pill/Pill";
import UserDetailsCard, { UserDetailsCardVariant } from "../../../../../Components/UserDetailsCard/UserDetailsCard";
import LocationsModel from "../../../../../Models/Locations/Locations.Model";
import MembersModel from "../../../../../Models/Member/Members.Model";
import NotesModel from "../../../../../Models/NotesMode/Notes.Model";
import TenantsModel from "../../../../../Models/Tenants/Tenants.Model";
import UserDetailsNote from "../Components/UserDetails.Note.Component";
import UserDetailsEditCustomerDetailsView from "./UserDetails.EditCustomerDetails.View";

interface UserDetailsCustomerDetailsViewProps {
  user: UserInterface;
  userVars: UserVarsMap | undefined;
  userCardVariant?: UserDetailsCardVariant;
}

export function UserDetailsCustomerDetailsView({ user, userCardVariant, userVars }: UserDetailsCustomerDetailsViewProps) {
  const [isEditing, setIsEditing] = useState(false);
  const tabs = ["Edit Customer Details"];
  const { notes } = NotesModel.useState((s) => s);

  const { tenants } = TenantsModel.useState((s) => s);
  const { membersMap } = MembersModel.useState((s) => s);

  const pinnedNotes = useMemo(() => {
    const pinnedNotesArr = notes[user.id]?.filter((note) => note.pinned) || [];

    const sortedNotes = pinnedNotesArr.sort((a, b) => {
      // @ts-ignore
      const dateA = a.updatedAt ? new Date(a.createdAt) : new Date(a.updatedAt);
      // @ts-ignore
      const dateB = b.updatedAt ? new Date(b.createdAt) : new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });
    return sortedNotes;
  }, [notes, user.id]);

  const locationId = user.locationId
    ? user.locationId
    : userVars && userVars[UserVarKey.Location]
      ? parseInt(userVars[UserVarKey.Location])
      : undefined;

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    TenantsModel.getTenantsByUserId(user.id);
    NotesModel.loadForUser(user.id, false);
  }, [locationId, user.id]);

  const filteredTenants = useMemo(() => {
    if (!locationId) {
      return [];
    }
    return tenants?.filter((t) => t.locationId === locationId) || [];
  }, [tenants, locationId]);

  const handleSetIsEditing = () => {
    setIsEditing(!isEditing);
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        {user && userVars && <UserDetailsCard user={user} handleSetIsEditing={handleSetIsEditing} variant={userCardVariant} />}
        {pinnedNotes.length > 0 && (
          <AnimatePresence initial={false} mode="popLayout">
            <div className="flex flex-col gap-2">
              {pinnedNotes.map((note) => (
                <UserDetailsNote key={note.id} note={note} membersMap={membersMap} />
              ))}
            </div>
          </AnimatePresence>
        )}
        {userVars &&
          filteredTenants.length > 0 &&
          filteredTenants?.map((tenant: TenantInterface) => {
            return <DataCardTenant key={tenant.id} tenant={tenant} userVars={userVars} />;
          })}
        {userVars && (!filteredTenants.length || userVars[UserVarKey.Location]) && (
          <DataCardLocation
            title="Prospect Profile"
            locationId={user && user.locationId ? user.locationId : undefined}
            pills={[
              <Pill key={`pill-${user.locationId}`} type={PillType.ORANGE} size={PillSize.SMALL} style={PillStyle.DARK} text="Selected"></Pill>
            ]}
            variant="secondary"
          />
        )}
      </div>
      <DetailsPanel
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        renderTab={(tab, _, isPanelOpen) => {
          switch (tab) {
            case "Edit Customer Details":
              return <UserDetailsEditCustomerDetailsView isPanelOpen={isPanelOpen} user={user} userVars={userVars} />;
            default:
              return null;
          }
        }}
        tabs={tabs}
      />
    </>
  );
}
